import React, { useEffect, useState, useRef } from 'react';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
    useIntl,
} from 'react-intl';
import { connect } from 'react-redux';
import { getUsernameAvailable } from '../../../api/user-api';
import { Navigation } from '../../../global-functions';
import { RootState } from '../../../store';
import { EMPTY_GUID } from '../../../models/common';
import { ICompanyAccount } from '../../../models/company-account';
import { ISession } from '../../../models/pro-sites';
import { IBucket } from '../../../models/prostatic';
import { IRole } from '../../../models/role';
import { IUserLocation, IUserModel } from '../../../models/user-model';
import * as ApiActions from '../../../store/api/api-status-action';
import * as companyActions from '../../../store/company/company-actions';
import * as userActions from '../../../store/user/user-actions';
import DeleteUserModal from './delete-user-modal';
import EditUserForm, { UserFormErrors } from './edit-user-form';
import { IValidationHandles } from '../../../models/validation-handles';

let isMoving = false;

const defaultUser = {
    firstName: '',
    lastName: '',
    directoryUser: '',
    confirmDirectoryUser: '',
    buckets: [],
    accounts: [],
    role: 2,
    preferences: { emailFlag: true },
};

interface IEditUserProps {
    session: ISession;
    currentUser?: IUserModel;
    companyAccounts?: ICompanyAccount[];
    companyBuckets: IBucket[];
    companyRoles: IRole[];
    saveError: string | null;
    loadUser: (uid: string) => Promise<void>;
    saveUser: (user: IUserModel) => Promise<boolean>;
    deleteUser: (user: IUserModel) => Promise<boolean>;
    loadCompanyAccounts: (id?: string) => Promise<void>;
    loadCompanyBuckets: (id?: string) => Promise<void>;
    loadCompanyRoles: (id?: string) => Promise<void>;
    setIsBusy: (isBusy: boolean) => Promise<void>;
}

const GotoManageUsers = (companyId: string, isManager: boolean) => {
    let url;
    if (companyId) {
        url = `/my-account/manage-companies/manage?c=${companyId}`;
    } else if (isManager) {
        url = '/my-account/manage-employee-access';
    } else {
        url = '/my-account/manage-users';
    }
    Navigation.pushExternal(url);
};

export const EditUser = ({
    session,
    currentUser,
    companyAccounts,
    companyBuckets,
    companyRoles,
    saveError,
    loadUser,
    saveUser,
    deleteUser,
    loadCompanyAccounts,
    loadCompanyBuckets,
    loadCompanyRoles,
    setIsBusy,
}: IEditUserProps) => {
    const intl = useIntl();
    const [editMode, setEditMode] = useState(false);
    const [companyId, setCompanyId] = useState('');
    const [userState, setUserState] = useState({ ...currentUser! });
    const [saving, setSaving] = useState(false);
    const [errorsState, setErrorsState] = useState<UserFormErrors>({});
    const [showModal, setShowModal] = useState(false);
    const formRef = useRef<IValidationHandles>(null);
    const isManager = session.buckets.includes(24);
    useEffect(() => {
        // eslint-disable-next-line no-restricted-globals
        const params = new URLSearchParams(location.search);
        const editUser = params.get('u');
        const cId = params.get('c') || '';
        if (cId) {
            setCompanyId(cId);
        }

        if (editUser) {
            setEditMode(true);
            loadUser(editUser);
        } else {
            setUserState((prevState) => {
                const notificationPreferences = {
                    phoneNumber: '',
                    emailFlag: true,
                    smsFlag: false,
                    otherNotificationEmail: '',
                };

                return {
                    ...prevState,
                    ...defaultUser,
                    notificationPreferences,
                    companyGuid: cId || EMPTY_GUID,
                };
            });
        }
        if (companyBuckets.length === 0) {
            loadCompanyBuckets(cId);
        }
        if (companyRoles.length === 0) {
            loadCompanyRoles(cId);
        }
        loadCompanyAccounts(cId);
    }, []);

    useEffect(() => {
        if (!editMode && companyAccounts!.length > 0) {
            const selectedAccounts: IUserLocation[] = companyAccounts!.map(
                (c) => {
                    return {
                        billTo: c.fullAccountNumber,
                        shipTo: null,
                        location: null,
                    };
                }
            );

            setUserState((prevState) => ({
                ...prevState,
                accounts: selectedAccounts,
            }));
        }
    }, [companyAccounts!]);

    useEffect(() => {
        if (currentUser) {
            const user = currentUser.notificationPreferences
                ? currentUser
                : {
                      ...currentUser,
                      notificationPreferences: { emailFlag: true },
                  };
            setUserState((prevState) => {
                return {
                    ...prevState,
                    ...user,
                    companyGuid: companyId || EMPTY_GUID,
                };
            });
        }
    }, [currentUser]);

    useEffect(() => {
        if (!editMode && companyRoles.length > 0) {
            setUserState((prevState) => ({
                ...prevState,
                buckets: companyRoles.find((x) => x.id === 2)!.buckets,
            }));
        }
    }, [companyRoles.length, editMode]);

    useEffect(() => {
        if (saveError) {
            setErrorsState((prevState) => ({
                ...prevState,
                onSave: saveError!,
            }));
        }
    }, [saveError]);

    const handleErrorsChange = (errors: UserFormErrors) => {
        setErrorsState(errors);
    };

    const handleChange = (event: any) => {
        const { name, value } = event.target;

        setUserState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleDelete = () => {
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
    };

    const handleModalAccept = async () => {
        setShowModal(false);

        setSaving(true);

        const response = await deleteUser(currentUser!);
        setSaving(false);

        if (!response) {
            return;
        }

        setIsBusy(true);

        GotoManageUsers(companyId, isManager);
    };

    const handleMove = () => {
        setIsBusy(true);
        isMoving = true;
    };

    const formIsValid = (): boolean => {
        return formRef.current ? formRef.current!.isValid() : false;
    };

    const handleSave = async (event: React.ChangeEvent) => {
        event.preventDefault();

        if (!formIsValid()) {
            return;
        }

        if (!editMode) {
            const usernameAvailableResult = await getUsernameAvailable(
                userState.directoryUser
            );
            if (!usernameAvailableResult.ok || !usernameAvailableResult.data) {
                setErrorsState({
                    directoryUser: intl.formatMessage({
                        id: 'User_name_is_not_available',
                        defaultMessage: 'User name is not available',
                    }),
                });
                return;
            }
        }

        setSaving(true);

        const response = await saveUser(userState);

        if (!response) {
            setSaving(false);
            return;
        }

        setIsBusy(true);

        if (!isMoving) {
            GotoManageUsers(companyId, isManager);
        } else {
            const url = '/my-account/manage-users/move?u=' + userState.guid;

            Navigation.pushExternal(url);
            isMoving = false;
        }
    };

    const handleCancel = () => {
        GotoManageUsers(companyId, isManager);
    };

    return (
        <>
            {Object.keys(userState).length > 0 &&
                companyAccounts &&
                companyBuckets.length > 0 &&
                companyRoles.length > 0 && (
                    <>
                        {editMode && <h1>{userState.directoryUser}</h1>}
                        {!editMode && (
                            <h1>
                                <FormattedMessage
                                    id="Add_New_User"
                                    defaultMessage="Add New User"
                                />
                            </h1>
                        )}
                        <EditUserForm
                            session={session}
                            user={userState}
                            editMode={editMode}
                            companyAccounts={companyAccounts!}
                            companyBuckets={companyBuckets}
                            companyRoles={companyRoles}
                            errors={errorsState}
                            saving={saving}
                            onChange={handleChange}
                            onDelete={handleDelete}
                            onMove={handleMove}
                            onSave={handleSave}
                            onCancel={handleCancel}
                            onErrorsChanged={handleErrorsChange}
                            ref={formRef}
                        />
                    </>
                )}
            {showModal && (
                <DeleteUserModal
                    onAccept={handleModalAccept}
                    onClose={handleModalClose}
                />
            )}
        </>
    );
};

const mapDispatchToProps = {
    loadCompanyAccounts: companyActions.loadCompanyAccounts,
    loadCompanyBuckets: companyActions.loadCompanyBuckets,
    loadCompanyRoles: companyActions.loadCompanyRoles,
    loadUser: userActions.loadUser,
    saveUser: userActions.saveUser,
    deleteUser: userActions.deleteUser,
    setIsBusy: ApiActions.setIsBusy,
};

const mapStateToProps = (state: RootState) => {
    return {
        companyAccounts: state.companyAccounts
            ? state.companyAccounts.data!
            : undefined,
        companyBuckets: state.companyBuckets ? state.companyBuckets.data! : [],
        companyRoles: state.companyRoles ? state.companyRoles.data! : [],
        currentUser: state.currentUser ? state.currentUser.data! : undefined,
        session: state.session!.data!,
        saveError:
            state.currentUser && state.currentUser.message
                ? state.currentUser.message!
                : null,
    };
};

const editUserContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(React.memo(EditUser));

export default editUserContainer;
